import React from 'react';

interface LevelBreakdownItem {
  level: number;
  total: number;
  learned: number;
  inProgress: number;
  notStarted: number;
  percent: number;
}

interface StatsProps {
  stats: {
    total: number;
    learned: number;
    inProgress: number;
    notStarted: number;
    currentDifficulty?: number;
    difficultyBreakdown?: LevelBreakdownItem[];
  };
}

const Stats: React.FC<StatsProps> = ({ stats }) => {
  const progressPercentage = stats.total > 0 ? (stats.learned / stats.total) * 100 : 0;

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">İlerleme Durumu</h2>
      
      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>Genel İlerleme</span>
          <span>{progressPercentage.toFixed(1)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div 
            className="bg-gradient-to-r from-primary to-secondary h-3 rounded-full transition-all duration-300"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>

      {/* Mevcut Zorluk Seviyesi */}
      {stats.currentDifficulty && (
        <div className="mb-6 p-4 bg-purple-50 rounded-lg border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-purple-800">Mevcut Zorluk Seviyesi</h3>
              <p className="text-sm text-purple-600">
                {stats.currentDifficulty === 1 && "Seviye 1: Temel Kelimeler"}
                {stats.currentDifficulty === 2 && "Seviye 2: Orta Seviye"}
                {stats.currentDifficulty === 3 && "Seviye 3: İleri Seviye"}
                {stats.currentDifficulty === 4 && "Seviye 4: Uzman Seviye"}
                {stats.currentDifficulty === 5 && "Seviye 5: Master Seviye"}
              </p>
            </div>
            <div className="text-3xl font-bold text-purple-600">
              {stats.currentDifficulty}
            </div>
          </div>
        </div>
      )}

      {/* İstatistik Kartları */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="text-center p-4 bg-blue-50 rounded-lg">
          <div className="text-2xl font-bold text-primary">{stats.total}</div>
          <div className="text-sm text-gray-600">Toplam Kelime</div>
        </div>
        
        <div className="text-center p-4 bg-green-50 rounded-lg">
          <div className="text-2xl font-bold text-secondary">{stats.learned}</div>
          <div className="text-sm text-gray-600">Öğrenilen</div>
        </div>
        
        <div className="text-center p-4 bg-yellow-50 rounded-lg">
          <div className="text-2xl font-bold text-warning">{stats.inProgress}</div>
          <div className="text-sm text-gray-600">Öğreniliyor</div>
        </div>
        
        <div className="text-center p-4 bg-gray-50 rounded-lg">
          <div className="text-2xl font-bold text-gray-600">{stats.notStarted}</div>
          <div className="text-sm text-gray-600">Başlanmamış</div>
        </div>
      </div>

      {/* Seviye Bazlı İlerleme */}
      {stats.difficultyBreakdown && stats.difficultyBreakdown.length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Seviye Bazlı İlerleme</h3>
          <div className="space-y-3">
            {stats.difficultyBreakdown.map((lvl) => (
              <div key={lvl.level} className="p-4 bg-gray-50 rounded-lg border">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-medium text-gray-800">Seviye {lvl.level}</div>
                  <div className="text-sm text-gray-600">%{lvl.percent.toFixed(1)} tamamlandı</div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                  <div
                    className="bg-primary h-2 rounded-full"
                    style={{ width: `${Math.min(100, Math.max(0, lvl.percent))}%` }}
                  />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-gray-700">
                  <div>
                    <span className="font-semibold">Toplam:</span> {lvl.total}
                  </div>
                  <div>
                    <span className="font-semibold">Öğrenilen:</span> {lvl.learned}
                  </div>
                  <div>
                    <span className="font-semibold">Öğreniliyor:</span> {lvl.inProgress}
                  </div>
                  <div>
                    <span className="font-semibold">Başlanmamış:</span> {lvl.notStarted}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Motivasyon Mesajı */}
      <div className="mt-6 p-4 bg-gradient-to-r from-primary to-secondary rounded-lg text-white text-center">
        <p className="font-semibold">
          {stats.learned === 0 && "İlk kelimeni öğrenmeye başla!"}
          {stats.learned > 0 && stats.learned < 10 && "Harika gidiyorsun! Devam et!"}
          {stats.learned >= 10 && stats.learned < 50 && "Muhteşem! Kelime haznen büyüyor!"}
          {stats.learned >= 50 && stats.learned < 100 && "İnanılmaz! Sen bir kelime ustasısın!"}
          {stats.learned >= 100 && "Efsane! İngilizce konuşmaya hazırsın!"}
        </p>
      </div>
    </div>
  );
};

export default Stats;
